import { doc, serverTimestamp, setDoc } from 'firebase/firestore';
import { Platform } from 'react-native';
import { auth, firestore } from '../firebase';

// Centralized source vocabulary — every event-detail navigation entry
// point must use one of these, never an ad-hoc string, so the dashboard's
// (future) source breakdown stays meaningful. `search` has no current call
// site (no dedicated search-results screen opens event details today) but
// stays defined for forward compatibility.
export const ANALYTICS_SOURCES = {
  FEED: 'feed',
  // Events screen surfaces. Every event-card tap on the Events screen is
  // attributed to exactly one of these, so the dashboard can compare
  // admin-curated weekly features, personalised picks and the plain list.
  // (`feed` above stays for StudiZone, which has no carousels.)
  WEEKLY_FEATURED: 'weekly_featured',
  TOP_PICKS_FOR_YOU: 'top_picks_for_you',
  // Seasonal/editorial collections (src/components/PromotionsSection.tsx).
  PROMOTION: 'promotion',
  REGULAR_FEED: 'regular_feed',
  MAP: 'map',
  SEARCH: 'search',
  PARTNER_PROFILE: 'partner_profile',
  PLAN: 'plan',
  RECOMMENDATION: 'recommendation',
  SHARED_LINK: 'shared_link',
  NOTIFICATION: 'notification',
  UNKNOWN: 'unknown',
} as const;

export type AnalyticsSource = (typeof ANALYTICS_SOURCES)[keyof typeof ANALYTICS_SOURCES];

// The Partner Dashboard's single admin account — excluded so the team's own
// browsing of the live app never inflates a real event's tap count. Extend
// this set if more internal/test accounts are introduced later.
const EXCLUDED_ANALYTICS_UIDS = new Set<string>([
  'D6frG30UBhWTO7kfwDGdNwTmCLj2', // tonyte.official@gmail.com
]);

// A host's own interactions with their own event aren't audience
// engagement (the server discards them too — functions/index.js
// isExcludedActor); skip the write entirely.
function isOwnEvent(uid: string | undefined, organizerId?: string | null) {
  return Boolean(uid && organizerId && uid === organizerId);
}

const DEDUP_BUCKET_MS = 5000; // matches the "2-5 second" rapid-retap window
const RECENT_TRACK_MEMORY_MS = 10000;

// Regenerated per app launch — a lightweight per-session id, not a
// persisted device/installation id. Sufficient for the dedup window and
// for a future source breakdown; nothing else needs it to survive a
// restart, so there's no reason to add AsyncStorage persistence for it.
const sessionId = `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;

// eventId -> last-tracked-at (ms). Backs wasRecentlyTracked(), which
// EventDetailScreen's deep-link fallback uses to avoid re-tracking an open
// that an in-app call site (or the notification handler) already recorded
// a moment earlier.
const recentlyTracked = new Map<string, number>();

function pruneRecentlyTracked(now: number) {
  for (const [key, trackedAt] of recentlyTracked) {
    if (now - trackedAt > RECENT_TRACK_MEMORY_MS) recentlyTracked.delete(key);
  }
}

export function wasRecentlyTracked(eventId: string): boolean {
  const now = Date.now();
  pruneRecentlyTracked(now);
  const trackedAt = recentlyTracked.get(eventId);
  return typeof trackedAt === 'number' && now - trackedAt <= RECENT_TRACK_MEMORY_MS;
}

type TrackEventDetailOpenArgs = {
  eventId: string;
  organizerId?: string | null;
  source: AnalyticsSource;
};

/**
 * Fire-and-forget: records that a user tapped into (or is about to open)
 * an event's detail screen. Never throws — a tracking failure must never
 * block navigation. Deduplicated server-side via a doc-ID idempotency key
 * (session + event + 5s bucket), so a rapid duplicate write lands on the
 * same document and the backend
 * aggregation trigger, which only fires on genuine creation, never
 * double-counts it.
 */
export function trackEventDetailOpen({ eventId, organizerId, source }: TrackEventDetailOpenArgs): void {
  if (!eventId) return;
  const uid = auth.currentUser?.uid;
  if (uid && EXCLUDED_ANALYTICS_UIDS.has(uid)) return;
  if (isOwnEvent(uid, organizerId)) return;

  const now = Date.now();
  recentlyTracked.set(eventId, now);

  const bucket = Math.floor(now / DEDUP_BUCKET_MS);
  const dedupKey = `${sessionId}_${eventId}_${bucket}`;

  const payload: Record<string, unknown> = {
    type: 'event_detail_open',
    eventId,
    source,
    platform: Platform.OS,
    sessionId,
    dedupKey,
    createdAt: serverTimestamp(),
  };
  if (organizerId) payload.organizerId = organizerId;
  if (uid) payload.userId = uid;

  setDoc(doc(firestore, 'eventAnalyticsRaw', dedupKey), payload).catch((error) => {
    console.warn('[Analytics] Failed to record event_detail_open', {
      eventId,
      source,
      error: error instanceof Error ? error.message : String(error),
    });
  });
}

// ---------------------------------------------------------------------------
// "Added to calendar" tracking — one row per person per event (fixed doc ID,
// create-only in firestore.rules), so a person adding the same event again
// never counts twice. Aggregated server-side by functions/index.js's
// aggregateEventCalendarAddEu into eventAnalytics/{eventId}.calendarAdds.
// ---------------------------------------------------------------------------

export function trackEventCalendarAdd({ eventId, organizerId }: { eventId: string; organizerId?: string | null }): void {
  const uid = auth.currentUser?.uid;
  if (!eventId || !uid || EXCLUDED_ANALYTICS_UIDS.has(uid) || isOwnEvent(uid, organizerId)) return;
  const payload: Record<string, unknown> = {
    type: 'event_calendar_add',
    eventId,
    platform: Platform.OS,
    userId: uid,
    createdAt: serverTimestamp(),
  };
  if (organizerId) payload.organizerId = organizerId;
  // A repeat add is an update of the same doc, which rules deny — expected.
  setDoc(doc(firestore, 'eventAnalyticsRaw', `event_calendar_add_${eventId}_${uid}`), payload).catch(() => undefined);
}

// ---------------------------------------------------------------------------
// Event share tracking — reuses this same module's session id, 5s dedup
// bucket, and create-only-doc-ID-as-idempotency-key pattern from
// trackEventDetailOpen above. Aggregated server-side by
// functions/index.js's aggregateEventShare into
// eventAnalytics/{eventId}.sharesCount — never by incrementing anything
// client-side.
// ---------------------------------------------------------------------------

export const SHARE_TYPES = {
  INTERNAL_CHAT: 'internal_chat',
  EXTERNAL: 'external',
} as const;

export type ShareType = (typeof SHARE_TYPES)[keyof typeof SHARE_TYPES];

// Deterministic per (event, destination, ~5s window) key, NOT a random id —
// this is what makes a duplicate invocation of the same underlying user
// action (a component rerender that re-fires a callback, a retried send
// that didn't actually create a new message) collide on the same Firestore
// doc ID instead of double-counting. `destination` is the target chatId for
// an internal-chat share (so sharing into two different chats in the same
// action still produces two distinct keys and counts twice, as intended),
// or the literal string 'external' for a native-share-sheet share (there is
// no reliable per-destination-app signal to key on there).
export function buildShareActionId(eventId: string, destination: string): string {
  const bucket = Math.floor(Date.now() / DEDUP_BUCKET_MS);
  return `share_${sessionId}_${eventId}_${destination}_${bucket}`;
}

type TrackEventShareArgs = {
  eventId: string;
  organizerId?: string | null;
  shareType: ShareType;
  destinationChatId?: string | null;
  shareActionId: string;
};

/**
 * Fire-and-forget, exactly like trackEventDetailOpen: never throws, so a
 * tracking failure can never block or interrupt the actual share the user
 * just completed. Call ONLY after the share is confirmed as complete as the
 * platform allows — a successful chat message send, or a native share
 * result reporting a completed action — never merely because a share
 * button/sheet was opened.
 */
export function trackEventShare({ eventId, organizerId, shareType, destinationChatId, shareActionId }: TrackEventShareArgs): void {
  if (!eventId || !shareActionId) return;
  const uid = auth.currentUser?.uid;
  if (uid && EXCLUDED_ANALYTICS_UIDS.has(uid)) return;
  if (isOwnEvent(uid, organizerId)) return;

  const payload: Record<string, unknown> = {
    type: 'event_share',
    eventId,
    shareType,
    platform: Platform.OS,
    sessionId,
    shareActionId,
    createdAt: serverTimestamp(),
  };
  if (organizerId) payload.organizerId = organizerId;
  if (destinationChatId) payload.destinationChatId = destinationChatId;
  if (uid) payload.userId = uid;

  setDoc(doc(firestore, 'eventAnalyticsRaw', shareActionId), payload).catch((error) => {
    console.warn('[Analytics] Failed to record event_share', {
      eventId,
      shareType,
      error: error instanceof Error ? error.message : String(error),
    });
  });
}
